import {
  faEllipsisVertical,
  faFingerprint,
  faMapLocation,
  faTrashCanCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import IWContextMenu from 'shared/components/thunderbolt/IWContextMenu'
import IWContextMenuItem from 'shared/components/thunderbolt/IWContextMenuItem'
import IWContextDivider from 'shared/components/thunderbolt/IWContextMenuItemDivider'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWToggle from 'shared/components/thunderbolt/IWToggle'
import {
  deleteOrgConfig,
  getLsAdminConfigs,
  OrgConfig,
  toggleActiveOrgConfig,
} from 'shared/loadSchedulingClient'
import DeleteOrgConfigModal from './DeleteOrgConfigModal'
import LsAdminPageSectionHeader from './LsAdminPageSectionHeader'
import { lsAdminConfigsTableSchema } from './tableSchemas'

type TableItem = {
  market: string
  brand: string
  enabled: JSX.Element
  contextMenu: JSX.Element
}

const tableColumns = lsAdminConfigsTableSchema

const USE_QUERY_KEY = 'ls-admin-configs'

export const LsAdminConfigurations = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [items, setItems] = useState<TableItem[]>([])
  const [searchParams] = useSearchParams()
  const [configIdToDelete, setConfigIdToDelete] = useState<string | undefined>(
    undefined,
  )
  const [isDeleteConfigModalOpen, setIsDeleteConfigModalOpen] = useState(false)
  const [currentViewableConfigId, setCurrentViewableConfigId] = useState<
    string | null
  >(null)

  const { data: configs, isLoading } = useQuery({
    queryKey: [USE_QUERY_KEY, currentViewableConfigId],
    queryFn: async () => {
      return getLsAdminConfigs()
    },
  })

  const mutation = useMutation({
    mutationKey: [USE_QUERY_KEY, 'toggle-enable'],
    mutationFn: ({
      configId,
      isActive,
    }: {
      configId: string
      isActive: boolean
    }) => {
      return toggleActiveOrgConfig(configId, isActive)
    },
    onSuccess: () => queryClient.invalidateQueries([USE_QUERY_KEY]),
  })

  const deleteConfigMutation = useMutation({
    mutationKey: [USE_QUERY_KEY, 'create'],
    mutationFn: (configId: string) => deleteOrgConfig(configId),
    onSuccess: () => queryClient.invalidateQueries([USE_QUERY_KEY]),
  })

  const mapper = (configurations: OrgConfig[]) => {
    let filtered = configurations || []

    if (currentViewableConfigId) {
      filtered = filtered.filter((c) => c.configId === currentViewableConfigId)
    }

    return orderBy(filtered || [], ['market', 'brand']).map((item, index) => ({
      market: item.market,
      brand: item.brand,
      enabled: (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <IWToggle
            id={index.toString()}
            checked={item.isActive}
            onChange={() => {
              mutation.mutateAsync({
                configId: item.configId,
                isActive: !item.isActive,
              })
            }}
          />
        </div>
      ),
      contextMenu: (
        <IWContextMenu
          positions={['bottom', 'top']}
          menu={[
            <IWContextMenuItem
              as="routerLink"
              key={`ls-config-view-creds-${item.configId}`}
              data-testid={`ls-config-view-creds-${item.configId}`}
              label={t('loadScheduling.contextMenus.viewCredentials')}
              icon={faFingerprint}
              to={`/admin/load-scheduling/credentials?configId=${item.configId}`}
            />,

            <IWContextMenuItem
              as="routerLink"
              key={`ls-config-view-la-${item.configId}`}
              data-testid={`ls-config-view-la-${item.configId}`}
              label={t('loadScheduling.contextMenus.viewLocationAttributes')}
              icon={faMapLocation}
              to={`/admin/load-scheduling/location-attributes?configId=${item.configId}`}
            />,

            <IWContextDivider />,

            <IWContextMenuItem
              as="button"
              key={`ls-config-delete-${item.configId}`}
              data-testid={`ls-config-delete-${item.configId}`}
              label={t('button.delete')}
              icon={faTrashCanCheck}
              disabled={item.isActive}
              onClick={() => {
                setConfigIdToDelete(item.configId)
                setIsDeleteConfigModalOpen(true)
              }}
            />,
          ]}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </IWContextMenu>
      ),
    }))
  }

  useEffect(() => {
    const queryParamsConfigId = searchParams.get('configId')
    setCurrentViewableConfigId(queryParamsConfigId || '')
  }, [])

  useEffect(() => {
    if (configs?.length) {
      setItems(mapper(configs))
    }
  }, [configs])

  return (
    <main>
      <LsAdminPageSectionHeader
        header={t('loadScheduling.titles.adminConfigs')}
        desc={t('loadScheduling.descriptions.adminConfigs')}
        buttonLabel={t('loadScheduling.labels.configuration')}
        onButtonClick={() => {
          navigate('/admin/load-scheduling/configs/create')
        }}
      >
        {isLoading && <IWLoading />}

        {Boolean(items?.length) && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={items}
            columns={tableColumns}
            showColumnSettings
          />
        )}
      </LsAdminPageSectionHeader>

      <DeleteOrgConfigModal
        isModalOpen={isDeleteConfigModalOpen}
        configId={configIdToDelete}
        onCancel={() => setIsDeleteConfigModalOpen(false)}
        onDelete={async (configId?: string) => {
          if (!configId) {
            return
          }

          await deleteConfigMutation.mutateAsync(configId)
          setIsDeleteConfigModalOpen(false)
        }}
      />
    </main>
  )
}

export default LsAdminConfigurations
